import React, { useState } from 'react';
import {
	View,
	TouchableOpacity,
	StyleSheet,
	useWindowDimensions
} from 'react-native';
import HybridQtyInput from './cartQuantity';
import { Theme, ThemeConsumer } from '@rneui/themed';
import { ZenIcon, ZenText, ZenToast } from 'zen-core-elements';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks';
import { shallowEqual } from 'react-redux';
import CommonService from '../../../shared/services/commonService';
import { deleteCartProduct } from '../../../redux/slices/newSellscreenSlice';
import moment from 'moment';
import { updateReturnProductStatus } from '../../../redux/slices/orderDetailSlice';
import { config } from '../../../shared/environment';
import CommonDialogBox from '../../../shared/commonComponents/commonDialogBox';
import Popover from 'react-native-popover-view';
import { appColors } from '../../../themes/appTheme';
import LineDiscountStrip from '../discounts/lineDiscountStrip';
import FreeItemLines from './freeItemLines';

export default function ModernCartCard({ onPressDetails, isWrite }: { onPressDetails: (id: number) => void, isWrite: boolean }) {
	const { width } = useWindowDimensions()
	const { id: locationId } = useAppSelector((state) => state?.common?.currentLocation)
	const dispatch = useAppDispatch()
	const products = useAppSelector((state) => state?.NewSellscreenSlice?.cartProducts, shallowEqual)
	const sellScreenType = useAppSelector((state) => state?.NewSellscreenSlice?.sellScreenType)
	const pricedLines = useAppSelector((state) => state?.NewSellscreenSlice?.pricedCart?.[locationId]?.lines)
	const { customerDetails, returnRequestData, returnRequestWholeOrderData } = useAppSelector(state => state?.NewSellscreenSlice)
	const currencySymbol = useAppSelector((state) => state?.common?.currentUser?.value?.purchasedCurrency?.currencySymbol) ?? '₹'
	const [declineReturnDialogVisible, setDeclineReturnDialogVisible] = useState<{ isVisible: boolean, id: number | null }>({ isVisible: false, id: null })
	const { value: currentUser } = useAppSelector(state => state?.common?.currentUser)
	const declineReturnDialogProps = {
		content: 'Are you sure you want to decline this return request product? ',
		button: [{ id: 1, name: 'Yes', color: appColors.primary, backgroundColor: appColors.light }, { id: 2, name: 'No', color: appColors.light, backgroundColor: appColors.primary }],
	}

	// Method which is used for restore confirmation
	const declineReturnConfirmation = (value: { name: String }) => {
		if (value?.name == 'No') {
			setDeclineReturnDialogVisible(prev => ({ ...prev, isVisible: false }))
		}
		else {
			setDeclineReturnDialogVisible(prev => ({ ...prev, isVisible: false }))
			deleteProduct(declineReturnDialogVisible?.id!)
		}
	}

	const deleteProduct = async (variantId: number) => {
		if (sellScreenType === "SELL") {
			dispatch(deleteCartProduct({ variantId, locationId }))
		} else {
			const matchedData = returnRequestWholeOrderData?.find(singleReturnData => singleReturnData?.variantSkuId === variantId)
			const data = {
				"todayDate": moment(new Date).format('MMMM DD, YYYY - hh:mm A'),
				"status": "Declined",
				"statusChange": "statusChange",
				"email": customerDetails?.email,
				"orderId": returnRequestData?.orderId ?? matchedData?.orderId,
				"productId": returnRequestData?.productId ?? matchedData?.productId,
				"orderProductId": returnRequestData?.orderProductId ?? matchedData?.orderProductId,
				"combinationName": returnRequestData?.combinationName ?? matchedData?.combinationName,
				"variantId": returnRequestData?.variantId ?? matchedData?.variantSkuId,
				"productName": returnRequestData?.productName ?? matchedData?.productName,
				"userName": returnRequestData?.userName,
				"customerId": customerDetails?.customerId,
				"quantity": returnRequestData?.quantity ?? matchedData?.quantity,
				"imageUrl": returnRequestData?.imageUrl,
				"action": returnRequestData?.action,
				"reason": returnRequestData?.reason ?? matchedData?.reason,
				"comments": returnRequestData?.comments ?? matchedData?.comments,
				"selectedModifiers": returnRequestData?.selectedModifiers,
				"customizedOrderId": returnRequestData?.customizedOrderId,
				"languageCode": returnRequestData?.languageCode
			}
			const payload = { storeId: config.storeId, orderId: returnRequestData?.orderId!, returnId: returnRequestData?.id!, data: data }
			await dispatch(updateReturnProductStatus(payload)).unwrap().then(() => {
				dispatch(deleteCartProduct({ variantId, locationId }))
			}).catch((err) => {
				ZenToast({ message: "Error while updating return status", type: "error" })
			})

		}
	}

	return (
		<ThemeConsumer>
			{({ theme }) => {
				const styles = style(theme)
				return (
					<>
					{products?.[locationId]?.map((product, index) => {
						const { discount, autoDiscount, tax, finalPrice, quantity, sku, weightCount, stock, price, variantId } = product
						const pricedLine = pricedLines?.find((line) => line.key === String(variantId))
						return (
							<View key={index} style={[styles.screenContainer, width < 600 && { paddingHorizontal: 0 }]}>

								<View style={styles.card}>

									<View style={[styles.accentBar, { backgroundColor: sellScreenType === "SELL" ? theme?.blue600 : theme?.red500 }]} />

									<View style={styles.contentContainer}>

										<View style={styles.headerRow}>
											<View style={styles.titleWrapper}>
												<ZenText content={product?.name + (product?.variantName ? ` - ${product?.variantName}` : "")} style={styles.productName} />
												<View style={styles.skuBadge}>
													{sku ? <ZenText content={`${"SKU: " + (sku || "---")}`} style={styles.skuText} /> : null}
													{sku ? <View style={styles.dotSeparator} /> : null}
													<View style={{ flexDirection: "row", alignItems: "center", justifyContent: "center", gap: 5 }}>
														<ZenText content={"Stock:"} style={styles.stockText} />
														<ZenText content={stock} style={[styles.stockText, { color: theme?.grey }]} />
													</View>
												</View>
											</View>

											{/* Top Right Actions */}
											<View style={styles.actionRow}>
												<TouchableOpacity style={styles.iconBtn} onPress={() => { onPressDetails(product?.productId) }}>
													<ZenIcon name="eye" type='octicon' color={theme?.blue600} size={15} />
												</TouchableOpacity>
												<TouchableOpacity style={[styles.iconBtn, styles.deleteBtn]} onPress={() => { sellScreenType === "SELL" ? deleteProduct(variantId) : setDeclineReturnDialogVisible({ isVisible: true, id: variantId }) }}>
													<ZenIcon name="delete-outline" type='material' color={theme?.red500} size={15} />
												</TouchableOpacity>
											</View>
										</View>
										{sellScreenType === "SELL" && pricedLine ? <LineDiscountStrip line={pricedLine} isWrite={isWrite} /> : null}
										<View style={styles.controlStrip}>

											<HybridQtyInput
												sellScreenType={sellScreenType}
												variantId={product?.variantId}
												quantity={quantity}
												stock={stock}
												weightCount={weightCount}
											/>

											<View style={styles.verticalDivider} />

											<View style={[styles.inputGroup, { flex: 1.5 }]}>
												<ZenText content="PRICE" style={[styles.label, { textTransform: "none" }]} />
												<View style={styles.plainInputWrapper}>
													<ZenText content={CommonService?.currencyStandard(currencySymbol, price?.toString()) + "/"} style={styles.plainInput} />
													<ZenText content={"per qty"} style={styles.currency} />
												</View>
											</View>
											<View style={styles.verticalDivider} />

											<View style={[styles.inputGroup, { flex: 1 }]}>
												<ZenText content={"Disc"} style={styles.label} />
												<ZenText content={CommonService?.currencyStandard(currencySymbol, ((sellScreenType === "SELL" ? discount : (autoDiscount || discount)) || 0).toString())} style={[styles.plainInput, { fontSize: theme?.errorFontSize }]} />
											</View>

											<View style={styles.verticalDivider} />

											<View style={[styles.inputGroup, { flex: 1.2 }]}>
												<ZenText content={"Tax"} style={styles.label} />
												<ZenText content={CommonService?.currencyStandard(currencySymbol, String(tax || 0))} style={[styles.plainInput, { fontSize: theme?.errorFontSize }]} />
											</View>

										</View>

										<View style={styles.footerRow}>

											<View style={styles.metaContainer}>
												<View style={styles.soldByTag}>
													<ZenText content={"By "} style={styles.soldByLabel} />
													<ZenText content={currentUser?.firstName} style={styles.soldByValue} />
												</View>
											</View>

											<View style={styles.totalWrapper}>
												<ZenText content={"Total"} style={styles.totalLabel} />
												<ZenText content={CommonService?.currencyStandard(currencySymbol, finalPrice?.toString())} style={styles.totalAmount} />
											</View>
										</View>

									</View>
								</View>
								<Popover isVisible={declineReturnDialogVisible?.isVisible} popoverStyle={{ width: width > 900 ? 500 : width > 600 ? 450 : 350, borderRadius: 10 }}>
									<CommonDialogBox type="OTHER" dialog={declineReturnDialogProps} confirmation={declineReturnConfirmation} />
								</Popover>
							</View >
						)
					})}
					{sellScreenType === "SELL" ? <FreeItemLines /> : null}
					</>
				)
			}}

		</ThemeConsumer>
	);
}

const style = (theme: Theme) => (
	StyleSheet.create({
		screenContainer: {
			paddingHorizontal: 10,
			marginBottom: 12,
			backgroundColor: theme?.light,
			flex: 1,
		},
		card: {
			backgroundColor: theme?.light,
			borderRadius: 16,
			flexDirection: 'row',
			overflow: 'hidden',
			shadowColor: theme?.disabledGray500,
			shadowOffset: { width: 0, height: 4 },
			shadowOpacity: 0.08,
			shadowRadius: 12,
			elevation: 4,
			borderWidth: 1,
			borderColor: theme.coolGray,
		},
		accentBar: {
			width: 4,

		},
		contentContainer: {
			flex: 1,
			paddingHorizontal: 13,
			paddingVertical: 10
		},

		// --- HEADER ---
		headerRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'flex-start',
			marginBottom: 10,
		},
		titleWrapper: {
			flex: 1,
			paddingRight: 10,
		},
		productName: {
			fontSize: theme?.smallerFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			marginBottom: 4,
		},
		skuBadge: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		skuText: {
			fontSize: theme?.badgeFontSize,
			color: theme?.grey,
			fontFamily: theme?.medium
		},
		dotSeparator: {
			width: 3,
			height: 3,
			borderRadius: 1.5,
			backgroundColor: theme?.grey,
			marginHorizontal: 6,
		},
		stockText: {
			fontSize: theme?.badgeFontSize,
			color: theme?.tealGreen,
			fontFamily: theme?.medium
		},

		// --- ACTIONS ---
		actionRow: {
			flexDirection: 'row',
			gap: 8,
		},
		iconBtn: {
			width: 32,
			height: 32,
			borderRadius: 8,
			backgroundColor: theme?.blue50,
			alignItems: 'center',
			justifyContent: 'center',
		},
		deleteBtn: {
			backgroundColor: theme?.red50,
		},

		// --- CONTROL STRIP (The Grid) ---
		controlStrip: {
			flexDirection: 'row',
			backgroundColor: theme?.iceWhite,
			borderRadius: 12,
			paddingHorizontal: 5,
			paddingVertical: 14,
			borderWidth: 1,
			borderColor: theme?.coolGray,
			alignItems: 'center',
			marginBottom: 10,
		},
		inputGroup: {
			alignItems: 'center',
			justifyContent: 'center',
		},
		verticalDivider: {
			width: 1,
			height: '60%',
			backgroundColor: theme?.coolGray,
			marginHorizontal: 8,
		},
		label: {
			fontSize: theme?.miniLabel,
			color: theme?.grey,
			marginBottom: 4,
			fontFamily: theme?.medium,
			textTransform: 'uppercase',
		},

		// Plain Inputs (Price, Tax, etc)
		plainInputWrapper: {
			flexDirection: 'row',
			alignItems: 'center',
		},
		currency: {
			color: theme?.grey,
			marginRight: 2,
			fontSize: 9, paddingLeft: 1, top: 2
		},
		plainInput: {
			fontSize: theme?.smallHeaderFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			// minWidth: 40,
		},

		// --- FOOTER ---
		footerRow: {
			flexDirection: 'row',
			justifyContent: 'space-between',
			alignItems: 'center',
			borderTopWidth: 1,
			borderTopColor: theme?.softSlate,
			paddingTop: 5,
			top: -2
		},
		metaContainer: {
			flexDirection: 'row',
			alignItems: 'center',
			gap: 12,
		},
		soldByTag: {
			flexDirection: 'row',
			alignItems: 'center',
			backgroundColor: theme?.softSlate,
			paddingHorizontal: 8,
			paddingVertical: 4,
			borderRadius: 6,
		},
		soldByLabel: {
			fontSize: theme?.miniLabel,
			fontFamily: theme?.medium,
			color: theme?.grey,
		},
		soldByValue: {
			fontSize: theme?.miniLabel,
			fontFamily: theme?.medium,
			color: theme.primary,
		},

		// Total Price Section
		totalWrapper: {
			flexDirection: "row",
			alignItems: 'center',
			gap: 6
		},
		totalLabel: {
			fontSize: theme?.miniLabel,
			color: theme?.grey,
			marginBottom: 0,
			fontFamily: theme?.medium
		},
		totalAmount: {
			fontSize: theme?.smallerFontSize,
			fontFamily: theme?.medium,
			color: theme?.lightDark,
			letterSpacing: -0.5,
		},
	})
)

